import { useMemo } from 'react'
import { DetalizationType, UsersSortType } from '@/features/analytics/constants'
import { useInviteLinksStore } from '../../model'
import { InviteLinksChartType } from '@/features/analytics/constants'
import { InviteLinkAnalytics } from '@synapex/prisma/client'
import { generateUniqueColor } from '../../utils'
import { LegendType } from 'recharts'
import { InviteLinkWithAnalyticsType } from '@/shared/api'
import { useParams } from 'react-router'

interface InviteLinksDataPoint {
    date: string
    [key: string]: number | string
}

export const useInviteLinksChartData = () => {
    const { groupId } = useParams<{ groupId: string }>()
    const numericGroupId = Number(groupId)
    const {
        selectedChartMetric,
        selectedLinksInviteByGroup,
        detalizationSortInvite,
        usersSortInvite,
        startDateInvite,
        endDateInvite,
    } = useInviteLinksStore()

    const inviteLinks = useMemo(() => {
        const selectedLinks = selectedLinksInviteByGroup[numericGroupId] || []
        return selectedLinks.map((link) => ({
            id: link.invite_link_id,
            name: link.invite_link_name,
            value: link.invite_link_name,
            color: generateUniqueColor(link.invite_link_id),
            type: 'line' as LegendType,
            title: link.external_channel
                ? link.external_channel?.telegram_channel_name
                : null,
            rknStatus: link.external_channel
                ? link.external_channel?.rkn_status
                : undefined,
        }))
    }, [selectedLinksInviteByGroup, numericGroupId])

    const inviteLinksData = useMemo(() => {
        const selectedLinks = selectedLinksInviteByGroup[numericGroupId] || []

        if (selectedLinks.length === 0) return []

        const processedLinks = processInviteLinks(selectedLinks)

        return calculateInviteLinksData(
            processedLinks,
            selectedChartMetric.value,
            usersSortInvite.value,
            detalizationSortInvite.value,
            startDateInvite,
            endDateInvite
        )
    }, [
        numericGroupId,
        selectedChartMetric,
        selectedLinksInviteByGroup,
        detalizationSortInvite,
        usersSortInvite,
        startDateInvite,
        endDateInvite,
    ])

    return { inviteLinksData, inviteLinks }
}

const processInviteLinks = (
    links: InviteLinkWithAnalyticsType[]
    // endDateInvite: string
): InviteLinkWithAnalyticsType[] => {
    const processedLinks = links.map((link) => {
        const linkCopy = { ...link }
        linkCopy.analytics = link.analytics ? [...link.analytics] : []
        // const endDate = new Date(endDateInvite);
        // const today = new Date();
        // const hasAnalytics = linkCopy.analytics.length > 0;
        linkCopy.analytics.sort(
            (a, b) =>
                new Date(a.created_at).getTime() -
                new Date(b.created_at).getTime()
        )
        let lastPostReachValue = 0
        for (let i = 1; i < linkCopy.analytics.length; i++) {
            const currentAnalytics = linkCopy.analytics[i]
            const previousAnalytics = linkCopy.analytics[i - 1]
            currentAnalytics.subscribed_count_delta = Math.max(
                currentAnalytics.subscribed_count -
                    previousAnalytics.subscribed_count,
                0
            )
            currentAnalytics.subscribed_today = Math.max(
                currentAnalytics.subscribed_count +
                    currentAnalytics.unsubscribed_count -
                    (previousAnalytics.subscribed_count +
                        previousAnalytics.unsubscribed_count),
                0
            )
            currentAnalytics.unsubscribed_today = Math.max(
                currentAnalytics.unsubscribed_count -
                    previousAnalytics.unsubscribed_count,
                0
            )
            currentAnalytics.premium_subscribed_today = Math.max(
                currentAnalytics.premium_subscribed_count -
                    previousAnalytics.premium_subscribed_count,
                0
            )
            currentAnalytics.premium_unsubscribed_today = Math.max(
                currentAnalytics.premium_unsubscribed_count -
                    previousAnalytics.premium_unsubscribed_count,
                0
            )
            currentAnalytics.without_premium_subscribed_today = Math.max(
                currentAnalytics.subscribed_count -
                    currentAnalytics.premium_subscribed_count -
                    (previousAnalytics.subscribed_count -
                        previousAnalytics.premium_subscribed_count),
                0
            )
            currentAnalytics.without_premium_unsubscribed_today = Math.max(
                currentAnalytics.unsubscribed_count -
                    currentAnalytics.premium_unsubscribed_count -
                    (previousAnalytics.unsubscribed_count -
                        previousAnalytics.premium_unsubscribed_count),
                0
            )
            if ((currentAnalytics.telegram_post_reach ?? 0) > 0) {
                lastPostReachValue = currentAnalytics.telegram_post_reach ?? 0
            }
            currentAnalytics.telegram_post_reach_delta = Math.max(
                (currentAnalytics.telegram_post_reach ?? lastPostReachValue) -
                    (previousAnalytics.telegram_post_reach ??
                        lastPostReachValue),
                0
            )
            // currentAnalytics.cps = Math.max((currentAnalytics.cps ?? 0) - (previousAnalytics.cps ?? 0), 0);
        }
        if (linkCopy.analytics.length > 0) {
            const firstAnalytics = linkCopy.analytics[0]
            firstAnalytics.subscribed_today = firstAnalytics.subscribed_count
            firstAnalytics.unsubscribed_today =
                firstAnalytics.unsubscribed_count
            firstAnalytics.premium_subscribed_today =
                firstAnalytics.premium_subscribed_count
            firstAnalytics.premium_unsubscribed_today =
                firstAnalytics.premium_unsubscribed_count
            firstAnalytics.without_premium_subscribed_today =
                firstAnalytics.subscribed_count -
                firstAnalytics.premium_subscribed_count
            firstAnalytics.without_premium_unsubscribed_today =
                firstAnalytics.unsubscribed_count -
                firstAnalytics.premium_unsubscribed_count
            // firstAnalytics.cps = firstAnalytics.cps;
        }
        return linkCopy
    })

    return processedLinks
}

const metricFieldAllUsersMap: { [key in InviteLinksChartType]: string } = {
    subscribedCount: 'subscribed_today',
    reach: 'telegram_post_reach_delta',
    CTR: 'ctr',
    CPM: 'cpm',
    CPS: 'cps',
    unsubscribedCount: 'unsubscribed_today',
    unsubscriptionPercentage: 'unsubscribed_percentage',
    total_CPS: 'total_cps',
    budget: 'budget',
    totalSubscribed: 'subscribed_count_delta',
}

const metricFieldPremiumUsersMap: { [key in InviteLinksChartType]: string } = {
    subscribedCount: 'premium_subscribed_today',
    reach: 'telegram_post_reach_delta',
    CTR: 'premium_ctr',
    CPM: 'cpm',
    CPS: 'premium_cps',
    unsubscribedCount: 'premium_unsubscribed_today',
    unsubscriptionPercentage: 'premium_unsubscribed_percentage',
    total_CPS: 'premium_total_cps',
    budget: 'budget',
    totalSubscribed: 'subscribed_count_delta',
}
const metricFieldWithoutPremiumUsersMap: {
    [key in InviteLinksChartType]: string
} = {
    subscribedCount: 'without_premium_subscribed_today',
    reach: 'telegram_post_reach_delta',
    CTR: 'without_premium_ctr',
    CPM: 'cpm',
    CPS: 'without_premium_cps',
    unsubscribedCount: 'without_premium_unsubscribed_today',
    unsubscriptionPercentage: 'without_premium_unsubscribed_percentage',
    total_CPS: 'without_premium_total_cps',
    budget: 'budget',
    totalSubscribed: 'subscribed_count_delta',
}

const metricAggregationType: {
    [key in InviteLinksChartType]: 'sum' | 'average'
} = {
    subscribedCount: 'sum',
    reach: 'sum',
    CTR: 'average',
    CPM: 'average',
    CPS: 'average',
    unsubscribedCount: 'sum',
    unsubscriptionPercentage: 'average',
    total_CPS: 'average',
    budget: 'sum',
    totalSubscribed: 'sum',
}

interface IntermediateDataPoint {
    [linkId: string]: {
        sum: number
        count: number
    }
}

const calculateInviteLinksData = (
    links: InviteLinkWithAnalyticsType[],
    selectedChartMetric: InviteLinksChartType,
    userSortInviteLinks: UsersSortType,
    detalization: DetalizationType,
    startDate: string,
    endDate: string
): InviteLinksDataPoint[] => {
    if (selectedChartMetric === 'budget') {
        // const now = new Date(); // Current date
        const start = new Date(startDate)
        start.setHours(0, 0, 0, 0)
        const end = new Date(endDate)
        end.setHours(23, 59, 59, 999)

        const dataPointsMap = new Map<string, InviteLinksDataPoint>()

        for (const link of links) {
            // let firstJoinedAt: Date | null = null;
            // if (!link.invite_link_name.includes("@")) {
            //     // firstJoinedAt = link.subscribers?.find(subscriber => subscriber.joined_at !== null)?.joined_at ?? null;
            //     // if (!firstJoinedAt) {
            //     //     continue;
            //     // }
            // } else
            if (!link.published_at || link.status != 'published') {
                continue
            }
            const creationDate = new Date(link.published_at)
            // const linkEndDate = new Date(Math.min(now.getTime(), endDate ? end.getTime() : now.getTime())); // Use the earlier of `now` or `endDate`

            if (!creationDate) {
                continue
            }

            const currentDate = new Date(creationDate)
            while (currentDate <= creationDate) {
                // linkEndDate
                const dateKey = getPeriodKey(currentDate, detalization)

                let dataPoint = dataPointsMap.get(dateKey)
                if (!dataPoint) {
                    dataPoint = { date: dateKey }
                    dataPointsMap.set(dateKey, dataPoint)
                }

                const key = link.invite_link_id.toString() // Ensure the key is a string
                dataPoint[key] =
                    ((dataPoint[key] as number) || 0) + (link.budget || 0)

                if (detalization === 'daily') {
                    currentDate.setDate(currentDate.getDate() + 1)
                } else if (detalization === 'weekly') {
                    currentDate.setDate(currentDate.getDate() + 7)
                } else if (detalization === 'monthly') {
                    currentDate.setMonth(currentDate.getMonth() + 1)
                }
            }
        }

        const dataPoints = Array.from(dataPointsMap.values())
        dataPoints.sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        )

        return dataPoints
    }

    // Determine which metric field to use based on the selected chart metric and user sort
    let metricField = metricFieldAllUsersMap[selectedChartMetric]
    const aggregationType = metricAggregationType[selectedChartMetric]

    if (userSortInviteLinks === 'premiumUsers') {
        metricField = metricFieldPremiumUsersMap[selectedChartMetric]
    } else if (userSortInviteLinks === 'noPremium') {
        metricField = metricFieldWithoutPremiumUsersMap[selectedChartMetric]
    }

    const start = new Date(startDate)
    start.setHours(0, 0, 0, 0)
    const end = new Date(endDate)
    end.setHours(23, 59, 59, 999)

    const dataPointsMap = new Map<string, IntermediateDataPoint>()

    for (let i = 0; i < links.length; i++) {
        const link = links[i]
        if (!link.analytics) {
            continue
        }

        for (let j = 0; j < link.analytics.length; j++) {
            const analytics = link.analytics[j]
            const analyticsDate = new Date(analytics.created_at)
            if (analyticsDate < start || analyticsDate > end) {
                continue
            }

            const dateKey = getPeriodKey(
                new Date(analytics.created_at),
                detalization
            )

            let dataPoint = dataPointsMap.get(dateKey)
            if (!dataPoint) {
                dataPoint = {}
                dataPointsMap.set(dateKey, dataPoint)
            }

            // Use the daily delta values (e.g., subscribed_today, unsubscribed_today)
            let value = analytics[metricField as keyof InviteLinkAnalytics]

            const linkId = link.invite_link_id
            if (typeof value === 'number') {
                if (!dataPoint[linkId]) {
                    dataPoint[linkId] = { sum: 0, count: 0 }
                }
                const metricData = dataPoint[linkId] as {
                    sum: number
                    count: number
                }

                if (metricField === 'ctr') {
                    value *= 100
                }

                metricData.sum += value
                metricData.count += 1
            }
        }
    }

    const inviteLinksData: InviteLinksDataPoint[] = []

    for (const [dateKey, dataPoint] of dataPointsMap.entries()) {
        const finalDataPoint: InviteLinksDataPoint = { date: dateKey }
        for (const [linkId, metricData] of Object.entries(dataPoint)) {
            if (linkId !== 'index') {
                const { sum, count } = metricData as {
                    sum: number
                    count: number
                }
                let finalValue = 0
                if (aggregationType === 'sum') {
                    finalValue = sum
                } else if (aggregationType === 'average') {
                    finalValue = count > 0 ? sum / count : 0
                }
                if (metricField === 'ctr') {
                    // finalValue = Math.round((finalValue + Number.EPSILON) * 100) / 100;
                    finalValue = Number(
                        parseFloat(finalValue.toString()).toFixed(2)
                    )
                }
                if (metricField === 'unsubscribed_percentage') {
                    // finalValue = Math.round((finalValue + Number.EPSILON) * 100) / 100;
                    finalValue = Number(
                        parseFloat((finalValue * 100).toString()).toFixed(2)
                    )
                }
                if (
                    metricField === 'cpm' ||
                    metricField === 'cps' ||
                    metricField === 'total_cps'
                ) {
                    finalValue = Math.round(finalValue)
                }
                finalDataPoint[linkId] = finalValue
            }
        }
        inviteLinksData.push(finalDataPoint)
    }

    inviteLinksData.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    )

    return inviteLinksData
}

const getPeriodKey = (date: Date, detalization: DetalizationType): string => {
    let key: string
    const dateCopy = new Date(date)
    const weekStart = new Date(dateCopy)

    switch (detalization) {
        case 'daily':
            key = dateCopy.toISOString().split('T')[0]
            break
        case 'weekly':
            weekStart.setDate(dateCopy.getDate() - dateCopy.getDay())
            key = weekStart.toISOString().split('T')[0]
            break
        case 'monthly':
            key = `${dateCopy.getFullYear()}-${String(dateCopy.getMonth() + 1).padStart(2, '0')}`
            break
        default:
            key = dateCopy.toISOString().split('T')[0]
    }
    return key
}
